import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, UserCheck, UserX, Key, Shield } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import ConfirmModal from '../components/common/ConfirmModal';
import AddUserModal from '../components/users/AddUserModal';
import EditUserModal from '../components/users/EditUserModal';
import PromoteUserModal from '../components/users/PromoteUserModal';
import SetPasswordModal from '../components/users/SetPasswordModal';
import { userAPI } from '../api/endpoints';
import { USER_ROLE_LABELS } from '../utils/constants';
import { useToastContext } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { USER_ROLES } from '../utils/constants';

const UserManagement = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const { permissions, user: currentUser } = useAuth();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  // Note: Do not return early before hooks. We'll render Access Denied below after hooks.

  // Fetch users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', searchTerm, roleFilter],
    queryFn: async () => {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (roleFilter) params.role = roleFilter;
      const response = await userAPI.getAll(params);
      return response.data;
    },
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => userAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setShowDeleteModal(false);
      setSelectedUser(null);
      toast.success('User deleted successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    },
  });

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: (data) => {
      console.log('🚀 UserManagement - Creating user with data:', data);
      return userAPI.create(data);
    },
    onSuccess: (response) => {
      console.log('✅ UserManagement - User created successfully:', response);
      queryClient.invalidateQueries(['users']);
      setShowAddModal(false);
      toast.success('User created successfully!');
    },
    onError: (error) => {
      console.error('❌ UserManagement - User creation failed:', error);
      console.error('❌ UserManagement - Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      // Enhanced error handling for 422 validation errors
      if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || {};
        console.error('Validation errors:', validationErrors);
        
        // Show specific validation errors
        const errorMessages = Object.values(validationErrors).flat();
        if (errorMessages.length > 0) {
          toast.error(`Validation Error: ${errorMessages.join(', ')}`);
        } else {
          toast.error('Please check your form data and try again.');
        }
      } else {
        toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to create user');
      }
    },
  });

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => userAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setShowEditModal(false);
      setSelectedUser(null);
      toast.success('User updated successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update user');
    },
  });

  // Promote user mutation
  const promoteMutation = useMutation({
    mutationFn: ({ id, role }) => userAPI.promoteUser(id, { new_role: role }),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setShowPromoteModal(false);
      setSelectedUser(null);
      toast.success('User role changed successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to change user role');
    },
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: (id) => userAPI.toggleStatus(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['users']);
      const message = response.data.message || 'User status updated!';
      toast.success(message);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update user status');
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: (id) => userAPI.resetPassword(id, { send_email: true }),
    onSuccess: (response) => {
      setShowResetPasswordModal(false);
      setSelectedUser(null);
      if (response.data.email_sent) {
        toast.success('Password reset link sent to user email!');
      } else {
        toast.warning('Reset token generated but email failed. Check server logs.');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to send reset link');
    },
  });

  // Set password mutation
  const setPasswordMutation = useMutation({
    mutationFn: ({ id, passwordData }) => userAPI.resetPassword(id, passwordData),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setShowSetPasswordModal(false);
      setSelectedUser(null);
      toast.success('Password set successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to set password');
    },
  });

  const handleAddUser = (data) => {
    createMutation.mutate(data);
  };

  const handleEditUser = (id, data) => {
    updateMutation.mutate({ id, data });
  };

  const handlePromoteUser = (id, role) => {
    promoteMutation.mutate({ id, role });
  };

  const handleDelete = () => {
    if (selectedUser) {
      deleteMutation.mutate(selectedUser.id);
    }
  };

  const handleToggleStatus = (user) => {
    toggleStatusMutation.mutate(user.id);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setShowResetPasswordModal(true);
  };

  const handleSetPassword = (user) => {
    setSelectedUser(user);
    setShowSetPasswordModal(true);
  };

  const handleConfirmSetPassword = (passwordData) => {
    if (selectedUser) {
      setPasswordMutation.mutate({ id: selectedUser.id, passwordData });
    }
  };

  const handleConfirmResetPassword = () => {
    if (selectedUser) {
      resetPasswordMutation.mutate(selectedUser.id);
    }
  };

  // Render Access Denied after hooks to keep hook order consistent
  if (!permissions.canCreateUsers()) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to manage users.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <Loading fullScreen text="Loading users..." />
      </Layout>
    );
  }

  const users = usersData?.users || [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600 mt-1">Manage system users and permissions</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="gap-2"
          >
            <Plus className="h-5 w-5" />
            Add User
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <Card.Content>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Roles</option>
                {/* Only show Super Admin filter option if current user is Super Admin */}
                {currentUser?.role === USER_ROLES.SUPER_ADMIN && (
                  <option value="super_admin">Super Administrator</option>
                )}
                <option value="event_admin">Event Administrator</option>
                <option value="usher">Event Usher</option>
                <option value="front_desk">Front Desk Staff</option>
                <option value="attendee">Attendee</option>
              </select>
            </div>
          </Card.Content>
        </Card>

        {/* Users Table */}
        <Card>
          <Card.Content>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className={`hover:bg-gray-50 ${currentUser.id === user.id ? 'bg-blue-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{user.full_name}</span>
                            {currentUser.id === user.id && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                You
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {USER_ROLE_LABELS[user.role]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {currentUser.id !== user.id ? (
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowPromoteModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-900"
                              title="Change Role"
                            >
                              <Shield className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              disabled
                              className="text-gray-400 cursor-not-allowed"
                              title="Cannot change your own role"
                            >
                              <Shield className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleSetPassword(user)}
                            className="text-green-600 hover:text-green-900"
                            title="Set Password"
                          >
                            <Key className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleResetPassword(user)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Reset Password"
                          >
                            <Key className="h-4 w-4" />
                          </button>
                          {permissions.canEditUser(user) && (
                            currentUser.id !== user.id ? (
                              <button
                                onClick={() => handleToggleStatus(user)}
                                className={user.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                                title={user.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                              </button>
                            ) : (
                              <button
                                disabled
                                className="text-gray-400 cursor-not-allowed"
                                title="Cannot deactivate yourself"
                              >
                                <UserX className="h-4 w-4" />
                              </button>
                            )
                          )}
                          {permissions.canEditUser(user) && (
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowEditModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          )}
                          {permissions.canDeleteUser(user) && (
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                          {!permissions.canEditUser(user) && !permissions.canDeleteUser(user) && (
                            <span className="text-gray-400 text-sm">No actions</span>
                          )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {users.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No users found</p>
                  </div>
                )}
            </div>
          </Card.Content>
        </Card>

        {/* Add User Modal */}
        <AddUserModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddUser}
          loading={createMutation.isPending}
        />

        {/* Edit User Modal */}
        <EditUserModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSubmit={handleEditUser}
          user={selectedUser}
          loading={updateMutation.isPending}
        />

        {/* Promote User Modal */}
        <PromoteUserModal
          isOpen={showPromoteModal}
          onClose={() => {
            setShowPromoteModal(false);
            setSelectedUser(null);
          }}
          onSubmit={handlePromoteUser}
          user={selectedUser}
          loading={promoteMutation.isPending}
        />

        {/* Set Password Modal */}
        <SetPasswordModal
          isOpen={showSetPasswordModal}
          onClose={() => {
            setShowSetPasswordModal(false);
            setSelectedUser(null);
          }}
          onSubmit={handleConfirmSetPassword}
          user={selectedUser}
          loading={setPasswordMutation.isPending}
        />

        {/* Reset Password Confirmation Modal */}
        <ConfirmModal
          isOpen={showResetPasswordModal}
          onClose={() => {
            setShowResetPasswordModal(false);
            setSelectedUser(null);
          }}
          onConfirm={handleConfirmResetPassword}
          title="Reset Password"
          message={`Send password reset link to ${selectedUser?.email}?`}
          confirmText="Send Reset Link"
          cancelText="Cancel"
          variant="warning"
          loading={resetPasswordMutation.isPending}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedUser(null);
          }}
          onConfirm={handleDelete}
          title="Delete User"
          message={`Are you sure you want to delete ${selectedUser?.full_name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          loading={deleteMutation.isPending}
        />
      </div>
    </Layout>
  );
};

export default UserManagement;
